import { native, type Product } from '@forest/native-services'
import {
  classifyPurchaseError,
  createSubscribeFlow,
  withFailure,
  type PurchasePresenter,
  type SubscriptionServiceLevelStatus,
} from '@forest/paywall-core'
import { wait } from '@forest/shared'
import { useMutation } from '@tanstack/solid-query'
import { closeDialog, setDialogLoading, showDialog } from '~/components/dialog'
import { reminderDialog } from '~/components/reminder-dialog'
import {
  AbortError,
  getAuthenticatedUserInfo,
} from '~/lib/get-authenticated-user-info'
import { platform } from '~/lib/params'
import { showRestoreErrorDialog } from '~/lib/restore-purchase'
import { showSubscriptionConflictDialog } from '~/lib/subscription-conflict-dialog'
import { m } from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime'
import { api } from '~/services/api'
import {
  $isReminderViewOpen,
  $receiptSystemAccountId,
  $subscribedView,
} from '~/store'

const REMINDER_OFFSET_MS = 48 * 60 * 60 * 1000 // 48 hours

function formatReminderDate(expiresAt: string, locale: string): string {
  const expiresDate = new Date(expiresAt)
  const reminderDate = new Date(expiresDate.getTime() - REMINDER_OFFSET_MS)
  return new Intl.DateTimeFormat(locale.replace('_', '-'), {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).format(reminderDate)
}

async function showReminderDialogIfNeeded(expiresAt: string) {
  if (import.meta.env.DEV) {
    const reminderDate = formatReminderDate(expiresAt, getLocale())
    await reminderDialog.open(reminderDate)
    return
  }

  let permissionResult
  try {
    permissionResult = await native.permission.getHasNotificationPermission()
  } catch {
    return
  }

  if (permissionResult.success && !permissionResult.data) {
    const reminderDate = formatReminderDate(expiresAt, getLocale())
    await reminderDialog.open(reminderDate)
  }
}

const flow = createSubscribeFlow<Product>({
  api,
  platform,
  native: {
    purchase: native.iap.purchase,
    closeTransaction: native.iap.closeTransaction,
    getAllUnclosedTransactions: native.iap.getAllUnclosedTransactions,
    getAllEntitlements: native.iap.getAllEntitlements,
    logEvent: native.general.logEvent,
    refreshServiceLevel: () => native.user.refreshServiceLevel(),
  },
  getUserId: async () => (await getAuthenticatedUserInfo()).user_id,
  getReceiptSystemAccountId: () => $receiptSystemAccountId.get(),
})

const presenter: PurchasePresenter<Product> = {
  // Reminder + store sets stay in the shell (they must run after the reminder
  // dialog and depend on the product's eligible_trial_days).
  success: () => {},
  error: (intent) => {
    switch (intent.kind) {
      case 'payment_timeout':
        showDialog({
          title: m.dialog_payment_timeout_title(),
          description: withFailure(
            m.dialog_payment_timeout_content({ error: intent.detail }),
            intent.failure,
          ),
          buttonText: m.OK(),
        })
        return
      case 'illegal_transaction_restore':
        showDialog({
          title: m.dialog_illegal_transaction_title(),
          description: withFailure(
            m.dialog_illegal_transaction_content(),
            intent.failure,
          ),
          buttonText: m.restore_purchase(),
          asyncAction: async () => {
            if (import.meta.env.DEV) {
              setDialogLoading(true)
              await wait(1000)
              closeDialog()
              showDialog({
                title: m.dialog_restore_success_title(),
                description: m.dialog_restore_success_content(),
                buttonText: m.awesome(),
                buttonAction: () => {
                  console.log('close')
                },
              })
              return
            }
            setDialogLoading(true)
            try {
              await flow.restore()
              closeDialog()
              showDialog({
                title: m.dialog_restore_success_title(),
                description: m.dialog_restore_success_content(),
                buttonText: m.awesome(),
                buttonAction: () => {
                  native.user.refreshServiceLevel()
                  native.general.close()
                },
              })
            } catch (restoreError) {
              closeDialog()
              await showRestoreErrorDialog(restoreError)
            }
          },
        })
        return
      case 'http_error':
        showDialog({
          title: m.dialog_unknown_error_title(),
          description: withFailure(
            m.dialog_unknown_error_content({
              error_code: intent.failure.code,
            }),
            intent.failure,
            { codeInDescription: true },
          ),
          buttonText: m.OK(),
        })
        return
      case 'no_internet':
        showDialog({
          title: m.dialog_no_internet_title(),
          description: withFailure(
            m.dialog_no_internet_content(),
            intent.failure,
          ),
          buttonText: m.OK(),
        })
        return
      case 'subscription_conflict':
        void showSubscriptionConflictDialog(intent.ownerUid, intent.failure)
        return
      case 'unknown':
        showDialog({
          title: m.dialog_unknown_error_title(),
          description: withFailure(
            m.dialog_unknown_error_content({
              error_code: intent.failure.code,
            }),
            intent.failure,
            { codeInDescription: true },
          ),
          buttonText: m.OK(),
        })
        return
      default:
        intent satisfies never
    }
  },
}

export function useSubscribeMutation(params?: {
  onSuccess?: (
    serviceLevelStatus: SubscriptionServiceLevelStatus | null,
    product: Product,
  ) => void
}) {
  const { onSuccess } = params ?? {}

  return useMutation(() => ({
    mutationFn: ({ product }: { product: Product }) => flow.run(product),
    onSuccess: (serviceLevelStatus, { product }) => {
      presenter.success(flow.handleSuccess(serviceLevelStatus, product))

      // Open the reminder dialog as a detached task so the mutation settles
      // immediately and the loading overlay — bound to isMutating — closes
      // before the dialog appears. Awaiting it here keeps the mutation pending
      // for the dialog's whole lifetime, leaving the higher-z-index overlay
      // covering it. The store sets + success callback must run after the
      // dialog, so they stay chained off the task.
      let task: Promise<void> = Promise.resolve()
      if (
        platform !== 'china_android' &&
        serviceLevelStatus?.service_level === 'subscription' &&
        (product.eligible_trial_days ?? 0) > 0
      ) {
        task = showReminderDialogIfNeeded(
          serviceLevelStatus.subscription.expires_at,
        )
      }

      task.then(() => {
        $isReminderViewOpen.set(false)
        $subscribedView.set({ isChinaAndroidTrial: false })
        onSuccess?.(serviceLevelStatus, product)
      })
    },
    onError: (error) => {
      if (error instanceof AbortError) return
      presenter.error(classifyPurchaseError(error), { error, flow })
    },
  }))
}
